import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

const PRICE_FILTER_FIELDS = ['filter.v.price.gte', 'filter.v.price.lte'];
const priceDisplayRates = new Map();
const PRICE_DISPLAY_CURRENCIES = ['TWD', 'THB', 'USD'];
const priceInputFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** @param {string} value */
function parsePriceAmount(value) {
  const normalized = value.trim().replace(/[,\s']/g, '');
  return /^\d+(?:\.\d{0,2})?$/.test(normalized) ? Number(normalized) : NaN;
}

/** @param {number} amount */
function formatPriceAmount(amount) {
  return priceInputFormatter.format(amount);
}

/** @param {string} baseCurrency */
function selectedPriceCurrency(baseCurrency) {
  const currencySelect = document.querySelector('select[name="doubly-currencies"]');
  const selectedCurrency = currencySelect instanceof HTMLSelectElement ? currencySelect.value : baseCurrency;
  return PRICE_DISPLAY_CURRENCIES.includes(selectedCurrency) ? selectedCurrency : baseCurrency;
}

/**
 * Shopify price filter parameters use the shop's base currency.
 * Include the half-cent display rounding interval for converted prices.
 * @param {number} amount - Amount entered in the displayed currency.
 * @param {number} rate - Displayed currency units per base currency unit.
 * @param {boolean} isMax - Whether this is the upper bound.
 * @param {number} rangeMaxCents - Shopify's maximum filter price in base currency cents.
 */
function displayedPriceToBaseCents(amount, rate, isMax, rangeMaxCents) {
  if (rate === 1) return Math.min(rangeMaxCents, Math.max(0, Math.round(amount * 100)));

  const displayedMaximum = Number(formatPriceAmount((rangeMaxCents / 100) * rate).replace(/,/g, ''));
  if (isMax && amount >= displayedMaximum) return rangeMaxCents;

  const roundingAllowance = isMax ? 0.005 : -0.005;
  const rawCents = ((amount + roundingAllowance) / rate) * 100;
  // A displayed minimum includes the first cent that rounds up to it; a
  // displayed maximum ends at the last cent that rounds down to it.
  const cents = Math.ceil(rawCents - 1e-8) - (isMax ? 1 : 0);
  return Math.min(rangeMaxCents, Math.max(0, cents));
}

/** @param {number} cents */
function baseCentsToParameter(cents) {
  return (cents / 100).toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    for (const name of PRICE_FILTER_FIELDS) {
      const rawValue = newParameters.get(name);
      if (rawValue === null) continue;
      const amount = parsePriceAmount(rawValue);
      if (!rawValue.trim() || !Number.isFinite(amount)) {
        newParameters.delete(name);
        continue;
      }

      const input = this.refs.facetsForm.querySelector(`input[name="${name}"]`);
      const savedBaseCents = Number(input?.dataset.baseValueCents);
      if (input?.value === input?.dataset.displayValue &&
        input?.dataset.baseValueCents && Number.isFinite(savedBaseCents)) {
        newParameters.set(name, baseCentsToParameter(savedBaseCents));
        continue;
      }
      const priceFacet = input?.closest('price-facet-component');
      const baseCurrency = priceFacet?.getAttribute('data-base-currency') || 'TWD';
      const requestedCurrency = selectedPriceCurrency(baseCurrency);
      const displayedCurrency = priceFacet?.getAttribute('data-display-currency') || baseCurrency;
      const rate = displayedCurrency === baseCurrency ? 1 : Number(priceFacet?.getAttribute('data-display-rate'));
      // When the converter is still loading or has just switched currencies,
      // preserve the existing base-currency filter rather than treating a
      // displayed amount as if it were already in the shop's base currency.
      if (requestedCurrency !== displayedCurrency || !(rate > 0 && Number.isFinite(rate))) {
        const existingValue = new URL(window.location.href).searchParams.get(name);
        if (existingValue === null) newParameters.delete(name);
        else newParameters.set(name, existingValue);
        continue;
      }
      const rangeMaxCents = Number(priceFacet?.getAttribute('data-base-range-max-cents')) || Infinity;
      const baseCents = displayedPriceToBaseCents(amount, rate, name.endsWith('.lte'), rangeMaxCents);
      newParameters.set(name, baseCentsToParameter(baseCents));
    }

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    const renderSection = () => sectionRenderer.renderSection(this.sectionId).then(() => {
      // Section rendering replaces product cards without a page load. Ask the
      // installed converter to process the new .money elements in the current
      // display currency, even when the selected currency has not changed.
      const currencySelect = document.querySelector('select[name="doubly-currencies"]');
      const baseCurrency = document.querySelector('price-facet-component')?.getAttribute('data-base-currency') || 'THB';
      if (currencySelect instanceof HTMLSelectElement && currencySelect.value !== baseCurrency) {
        currencySelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (viewTransition) {
      startViewTransition(renderSection, ['product-grid']);
    } else {
      renderSection();
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {number | undefined} */
  #currencyTimer;
  #displayCurrency = '';
  #displayRate = 0;
  #serverValues = ['', ''];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    // Other facet elements are defined later in this module. Wait until the
    // definitions have finished before updating the status component.
    queueMicrotask(() => {
      if (this.isConnected) this.#syncDisplayCurrency();
    });
    // The display converter may load after the theme or replace prices without
    // a change event. This also covers sections re-rendered after filtering.
    this.#currencyTimer = window.setInterval(() => {
      if (!document.hidden) this.#syncDisplayCurrency();
    }, 500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    window.clearInterval(this.#currencyTimer);
    this.#currencyTimer = undefined;
  }

  /** Keep the visible inputs in the selected display currency. */
  #syncDisplayCurrency() {
    const { minInput, maxInput } = this.refs;
    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return;

    const baseCurrency = this.dataset.baseCurrency || 'TWD';
    const currency = selectedPriceCurrency(baseCurrency);
    const rangeMaxCents = Number(this.dataset.baseRangeMaxCents);
    if (!Number.isFinite(rangeMaxCents)) return;

    let rate = 1;
    if (currency !== baseCurrency) {
      const baseAttribute = `doubly-currency-${baseCurrency.toLowerCase()}`;
      const currencyAttribute = `doubly-currency-${currency.toLowerCase()}`;
      const convertedPrice = this.querySelector(`.price-facet__highest-price [${baseAttribute}][${currencyAttribute}]`) ||
        document.querySelector(`[${baseAttribute}][${currencyAttribute}]`);
      const baseCents = Number(convertedPrice?.getAttribute(baseAttribute));
      const displayCents = Number(convertedPrice?.getAttribute(currencyAttribute));
      const rateKey = `${baseCurrency}:${currency}`;
      rate = baseCents > 0 && displayCents > 0 ? displayCents / baseCents : priceDisplayRates.get(rateKey) || 0;
      if (!(rate > 0)) return;
      priceDisplayRates.set(rateKey, rate);
    }

    this.#syncHighestPrice(currency, rate, rangeMaxCents);
    const summary = this.closest('details')?.querySelector('[ref="facetStatus"]');
    const inputs = [minInput, maxInput];
    const serverValues = inputs.map((input) => input.getAttribute('value') || '');
    const serverUpdated = Boolean(this.#displayCurrency) && inputs.some((input, index) =>
      serverValues[index] !== this.#serverValues[index] ||
      (input.value === serverValues[index] && input.value !== input.dataset.displayValue)
    );
    if (this.#displayCurrency === currency && Math.abs(this.#displayRate - rate) < 1e-12 &&
      summary?.getAttribute('data-display-currency') === currency && !serverUpdated) return;

    // If the shopper switches currency while an input is being edited, retain
    // the entered threshold before replacing the visible value.
    if (serverUpdated) {
      // Shopify's section renderer can reuse this element while replacing its
      // input attributes with fresh base-currency values. Those are not shopper edits.
      for (const [index, input] of inputs.entries()) {
        const amount = parsePriceAmount(serverValues[index]);
        input.dataset.baseValueCents = Number.isFinite(amount) ? String(Math.round(amount * 100)) : '';
      }
    } else if (this.#displayRate > 0) {
      for (const input of inputs) {
        if (input.value === input.dataset.displayValue) continue;
        const amount = parsePriceAmount(input.value);
        input.dataset.baseValueCents = Number.isFinite(amount)
          ? String(displayedPriceToBaseCents(amount, this.#displayRate, input === maxInput, rangeMaxCents))
          : '';
      }
    }

    this.#displayCurrency = currency;
    this.#displayRate = rate;
    this.#serverValues = serverValues;
    this.dataset.displayRate = String(rate);
    this.dataset.displayCurrency = currency;

    const formatBaseCents = (cents) => formatPriceAmount((cents / 100) * rate);
    const minBaseCents = minInput.dataset.baseValueCents;
    const maxBaseCents = maxInput.dataset.baseValueCents;
    minInput.value = minBaseCents ? formatBaseCents(Number(minBaseCents)) : '';
    maxInput.value = maxBaseCents ? formatBaseCents(Number(maxBaseCents)) : '';
    minInput.dataset.displayValue = minInput.value;
    maxInput.dataset.displayValue = maxInput.value;
    minInput.placeholder = '0';
    maxInput.placeholder = formatBaseCents(rangeMaxCents);
    minInput.dataset.min = '0';
    minInput.dataset.max = maxBaseCents ? formatBaseCents(Number(maxBaseCents)) : maxInput.placeholder;
    maxInput.dataset.min = minBaseCents ? formatBaseCents(Number(minBaseCents)) : '0';
    maxInput.dataset.max = maxInput.placeholder;

    for (const input of [minInput, maxInput]) {
      const label = input.parentElement?.querySelector('label');
      if (label) label.textContent = currency;
    }

    if (summary instanceof HTMLElement) {
      summary.dataset.displayCurrency = currency;
      summary.dataset.displayRangeMax = String(Math.round(rangeMaxCents * rate));
    }
    this.#updateSummary();
  }

  /** Show the converted maximum even when the currency app skips an AJAX-rendered facet. */
  #syncHighestPrice(currency, rate, rangeMaxCents) {
    const original = this.querySelector('.price-facet__highest-price .money');
    if (!(original instanceof HTMLElement)) return;

    let converted = this.querySelector('.price-facet__converted-highest');
    if (currency === (this.dataset.baseCurrency || 'TWD')) {
      original.hidden = false;
      if (converted instanceof HTMLElement) converted.hidden = true;
      return;
    }

    if (!(converted instanceof HTMLElement)) {
      converted = document.createElement('span');
      converted.className = 'price-facet__converted-highest';
      original.after(converted);
    }
    const amount = formatPriceAmount((rangeMaxCents / 100) * rate);
    converted.textContent = currency === 'USD' ? `$${amount} USD` :
      currency === 'TWD' ? `NT$${amount} TWD` : `${amount} ฿ THB`;
    original.hidden = true;
    converted.hidden = false;
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#syncDisplayCurrency();
    const baseCurrency = this.dataset.baseCurrency || 'TWD';
    if (this.#displayCurrency !== selectedPriceCurrency(baseCurrency) || !(this.#displayRate > 0)) return;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#rememberBaseValues();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = parsePriceAmount(input.value);
    const min = parsePriceAmount(input.getAttribute('data-min') ?? '');
    const max = parsePriceAmount(input.getAttribute('data-max') ?? '');

    if (!Number.isFinite(value)) {
      input.value = '';
      return;
    }
    if (Number.isFinite(min) && value < min) input.value = formatPriceAmount(min);
    else if (Number.isFinite(max) && value > max) input.value = formatPriceAmount(max);
    else input.value = formatPriceAmount(value);
  }

  /** Save the base-currency thresholds used when the display currency changes. */
  #rememberBaseValues() {
    const { minInput, maxInput } = this.refs;
    const rangeMaxCents = Number(this.dataset.baseRangeMaxCents);
    const rate = this.#displayRate || 1;
    for (const input of [minInput, maxInput]) {
      if (input.value === input.dataset.displayValue && input.dataset.baseValueCents) continue;
      const amount = parsePriceAmount(input.value);
      input.dataset.baseValueCents = Number.isFinite(amount)
        ? String(displayedPriceToBaseCents(amount, rate, input === maxInput, rangeMaxCents))
        : '';
      input.dataset.displayValue = input.value;
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
        if (input.closest('price-facet-component')) input.dataset.baseValueCents = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const baseCurrency = facetStatus.dataset.baseCurrency || 'TWD';
    const currency = facetStatus.dataset.displayCurrency || baseCurrency;
    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(
      maxInputValue,
      facetStatus.dataset.displayRangeMax || facetStatus.dataset.rangeMax
    );
    if (currency !== baseCurrency) {
      facetStatus.textContent = `${currency} ${formatPriceAmount(minInputNum / 100)}–${currency} ${formatPriceAmount(maxInputNum / 100)}`;
    } else {
      facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
    }
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const amount = parsePriceAmount(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : Number(fallback) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
